import { CustomFieldInputs, useCustomFields } from './CustomFields.tsx';
import { useEffect, useState } from 'react';
import { locale, t, tn } from '../i18n/index.ts';
import { action } from '../lib/api.ts';
import { money } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { useUi } from '../lib/ui-state.tsx';
import { Icon } from './Icon.tsx';
import { Button, Dialog, Field, IconButton } from './ui.tsx';

export interface CustomerRow {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
  allergies: string | null;
  note: string | null;
  birthday: string | null;
  marketing_at: string | null;
  vip: boolean;
  active: boolean;
  visits: number;
  spent: number;
  last_visit: string | null;
}
export interface CustomerCard {
  id: string;
  name: string;
  phone: string | null;
  allergies: string | null;
  note: string | null;
  vip: boolean;
  birthday: string | null;
  visits: number;
  spent: number;
}

export const initials = (name: string) =>
  name
    .split(/\s+/)
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase() || '?';

/** "+355691234567" → "+355 69 123 4567"; other numbers in groups of three. */
export function phoneText(phone: string | null): string {
  if (!phone) return '';
  const m = /^\+355(\d{2})(\d{3})(\d{3,4})$/.exec(phone);
  if (m) return `+355 ${m[1]} ${m[2]} ${m[3]}`;
  return phone.replace(/^(\+\d{2,3})(\d+)$/, (_, c: string, rest: string) => `${c} ${rest.replace(/(\d{3})(?=\d)/g, '$1 ')}`);
}

/** Customers matching a search, loaded as the person types. */
export function useCustomerSearch(search: string, filter: 'active' | 'vip' | 'archived' | 'all' = 'active', version = 0) {
  const { fail } = useUi();
  const [rows, setRows] = useState<CustomerRow[] | null>(null);
  useEffect(() => {
    const timer = setTimeout(
      () => void action<CustomerRow[]>('customers.list', { search: search.trim() || undefined, filter }).then(setRows, fail),
      search ? 200 : 0,
    );
    return () => clearTimeout(timer);
  }, [search, filter, version, fail]);
  return rows;
}

export function AllergyNote({ text, compact }: { text: string; compact?: boolean }) {
  return (
    <span className={`allergy${compact ? ' compact' : ''}`}>
      <Icon name="alert" size={compact ? 14 : 16} />
      <span>{t('cust.allergy', { text })}</span>
    </span>
  );
}

/** Find a customer or add a new one, then hand it back. */
export function CustomerPicker({ title, onPick, onClose }: { title: string; onPick: (c: CustomerCard) => Promise<void> | void; onClose: () => void }) {
  const { can } = useSession();
  const { fail } = useUi();
  const [search, setSearch] = useState('');
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const rows = useCustomerSearch(search);
  const pick = async (c: CustomerCard) => {
    setBusy(true);
    try {
      await onPick(c);
    } catch (err) {
      fail(err);
      setBusy(false);
    }
  };
  if (adding) {
    const digits = search.replace(/[^\d+]/g, '');
    const looksLikePhone = digits.length >= 6 && digits.length >= search.replace(/\s/g, '').length - 1;
    return (
      <CustomerForm
        title={t('cust.new')}
        initial={looksLikePhone ? { phone: search } : { name: search }}
        onClose={() => setAdding(false)}
        onSaved={(c) => pick({ ...c, visits: 0, spent: 0 })}
      />
    );
  }
  return (
    <Dialog
      title={title}
      onClose={onClose}
      footer={
        can('customers.edit') ? (
          <Button variant="secondary" icon="plus" onClick={() => setAdding(true)}>
            {t('cust.new')}
          </Button>
        ) : undefined
      }
    >
      <div className="search">
        <Icon name="search" size={18} />
        <input
          className="input"
          placeholder={t('cust.search')}
          aria-label={t('cust.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-autofocus
        />
        {search ? <IconButton icon="x" label={t('common.clear')} onClick={() => setSearch('')} /> : null}
      </div>
      <div className="pick-list" aria-busy={!rows || busy}>
        {rows?.length === 0 ? (
          <p className="muted pick-empty">{search ? t('cust.none_found', { q: search }) : t('cust.none_yet')}</p>
        ) : (
          rows?.slice(0, 12).map((c) => (
            <button key={c.id} className="pick-row" disabled={busy} onClick={() => void pick(c)}>
              <span className="avatar" aria-hidden="true">
                {initials(c.name)}
              </span>
              <span className="pick-main">
                <span className="strong">
                  {c.name} {c.vip ? <span className="vip">{t('cust.vip')}</span> : null}
                </span>
                <span className="muted">{[phoneText(c.phone), tn('cust.visits', c.visits)].filter(Boolean).join(' · ')}</span>
              </span>
              {c.allergies ? <AllergyNote text={c.allergies} compact /> : null}
            </button>
          ))
        )}
      </div>
    </Dialog>
  );
}

/** Add or change a customer's details. */
export function CustomerForm({
  title,
  initial,
  onClose,
  onSaved,
}: {
  title: string;
  initial?: Partial<CustomerRow>;
  onClose: () => void;
  onSaved: (c: CustomerRow) => void;
}) {
  const { fail, toast } = useUi();
  const [f, setF] = useState({
    name: initial?.name ?? '',
    phone: initial?.phone ? phoneText(initial.phone) : '',
    email: initial?.email ?? '',
    birthday: initial?.birthday ?? '',
    allergies: initial?.allergies ?? '',
    note: initial?.note ?? '',
    vip: initial?.vip ?? false,
    marketing: !!initial?.marketing_at,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tried, setTried] = useState(false);
  const cf = useCustomFields('customer.customer', initial?.id);
  const set = (k: keyof typeof f) => (v: string | boolean) => setF((x) => ({ ...x, [k]: v }));
  const save = async () => {
    setTried(true);
    if (f.name.trim().length < 2) {
      setError(t('cust.name_needed'));
      return;
    }
    if (!cf.valid) return;
    setBusy(true);
    try {
      const input = { ...f, phone: f.phone || null, email: f.email || null, birthday: f.birthday || null };
      const saved = initial?.id
        ? await action<CustomerRow>('customers.update', { customer: initial.id, ...input })
        : await action<CustomerRow>('customers.create', input);
      await cf.save(saved.id);
      toast(initial?.id ? t('cust.saved', { name: saved.name }) : t('cust.added', { name: saved.name }), 'ok');
      onSaved(saved);
    } catch (err) {
      fail(err);
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={title}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="check" busy={busy} onClick={save}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="form-grid">
        <Field label={t('cust.name')} error={error}>
          <input className="input" value={f.name} onChange={(e) => (setError(null), set('name')(e.target.value))} autoComplete="off" data-autofocus />
        </Field>
        <Field label={t('cust.phone')} hint={t('cust.phone_hint')}>
          <input className="input" type="tel" inputMode="tel" value={f.phone} onChange={(e) => set('phone')(e.target.value)} autoComplete="off" />
        </Field>
        <Field label={t('cust.email')}>
          <input className="input" type="email" value={f.email} onChange={(e) => set('email')(e.target.value)} autoComplete="off" />
        </Field>
        <Field label={t('cust.birthday')}>
          <input className="input" type="date" value={f.birthday} onChange={(e) => set('birthday')(e.target.value)} />
        </Field>
      </div>
      <Field label={t('cust.allergies')} hint={t('cust.allergies_hint')}>
        <input className="input" value={f.allergies} onChange={(e) => set('allergies')(e.target.value)} placeholder={t('cust.allergies_example')} />
      </Field>
      <Field label={t('cust.note')}>
        <textarea className="input" rows={2} value={f.note} onChange={(e) => set('note')(e.target.value)} />
      </Field>
      <label className="check-row">
        <input type="checkbox" checked={f.vip} onChange={(e) => set('vip')(e.target.checked)} />
        <span>
          <span className="strong">{t('cust.vip_label')}</span>
          <span className="muted">{t('cust.vip_hint')}</span>
        </span>
      </label>
      <label className="check-row">
        <input type="checkbox" checked={f.marketing} onChange={(e) => set('marketing')(e.target.checked)} />
        <span>
          <span className="strong">{t('cust.marketing')}</span>
          <span className="muted">{t('cust.marketing_hint')}</span>
        </span>
      </label>
      <CustomFieldInputs cf={cf} tried={tried} />
    </Dialog>
  );
}

/** The customer on an order, with allergies up front; add, change or remove. */
export function OrderCustomer({ order, customer, open, onChanged }: { order: string; customer: string | null; open: boolean; onChanged: () => void }) {
  const { can } = useSession();
  const { fail, toast } = useUi();
  const [card, setCard] = useState<CustomerCard | null>(null);
  const [picking, setPicking] = useState(false);
  useEffect(() => {
    if (!customer || !can('customers.view')) {
      setCard(null);
      return;
    }
    void action<CustomerCard>('customers.card', { customer }).then(setCard, fail);
  }, [customer, can, fail]);
  const canChange = open && can('customers.attach');
  const attach = async (c: CustomerCard | null) => {
    await action('customers.attach', { order, customer: c?.id ?? null });
    setPicking(false);
    toast(c ? t('cust.on_bill', { name: c.name }) : t('cust.off_bill'), 'ok');
    onChanged();
  };
  if (!customer && !canChange) return null;
  return (
    <div className={`order-customer${card?.allergies ? ' alert' : ''}`}>
      {customer && card ? (
        <>
          <span className="avatar sm" aria-hidden="true">
            {initials(card.name)}
          </span>
          <span className="order-customer-main">
            <span className="strong">
              {card.name} {card.vip ? <span className="vip">{t('cust.vip')}</span> : null}
            </span>
            <span className="muted">{card.visits ? t('cust.card_visits', { n: card.visits, spent: money(card.spent) }) : t('cust.first_visit')}</span>
            {card.allergies ? <AllergyNote text={card.allergies} compact /> : null}
            {card.note ? <span className="muted">{card.note}</span> : null}
          </span>
          {canChange ? (
            <span className="order-customer-actions">
              <IconButton icon="edit" label={t('cust.change')} onClick={() => setPicking(true)} />
              <IconButton icon="x" label={t('cust.remove')} onClick={() => void attach(null).catch(fail)} />
            </span>
          ) : null}
        </>
      ) : customer ? (
        <span className="muted">{t('common.loading')}</span>
      ) : (
        <button className="order-customer-add" onClick={() => setPicking(true)}>
          <Icon name="users" size={16} />
          {t('cust.add_to_bill')}
        </button>
      )}
      {picking ? <CustomerPicker title={t('cust.pick_title')} onPick={(c) => attach(c)} onClose={() => setPicking(false)} /> : null}
    </div>
  );
}

export function VisitStats({ visits, spent, average, last }: { visits: number; spent: number; average: number; last: string | null }) {
  return (
    <dl className="fiscal-stats">
      <div className="fiscal-stat">
        <dt>{t('cust.k_visits')}</dt>
        <dd>{visits}</dd>
      </div>
      <div className="fiscal-stat">
        <dt>{t('cust.k_spent')}</dt>
        <dd>{money(spent)}</dd>
      </div>
      <div className="fiscal-stat">
        <dt>{t('cust.k_average')}</dt>
        <dd>{money(average)}</dd>
      </div>
      <div className="fiscal-stat">
        <dt>{t('cust.k_last')}</dt>
        <dd className="small">{last ? new Intl.DateTimeFormat(locale(), { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(last)) : '—'}</dd>
      </div>
    </dl>
  );
}

